import { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hook";
import { useTranslation } from "../../localization";
import { ModerationFlows, RateLimitFlows, useImageErrors, useModerationErrors, useRateLimitErrors, } from "../../miscellaneous";
import { ABORT_ERROR_MESSAGE } from "../constants";
import { deleteConversationImage, resetConversationImageUploadState, uploadConversationImage, } from "../conversationSlice";
export const useConversationImageUpload = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { isImageUploading } = useAppSelector((state) => state.conversation);
    const [imageError, setImageError] = useState(null);
    const { getMessage: getRateLimitErrorMessage } = useRateLimitErrors();
    const { getMessage: getModerationErrorMessage } = useModerationErrors();
    const { getMessage: getImageErrorMessage } = useImageErrors();
    const resolveImageError = useCallback((errorPayload) => {
        const errorCode = errorPayload?.errorCode || "";
        const responseErrorMessage = errorPayload?.error || errorPayload?.message || t("error:fallbackError");
        const rateLimitErrorMessage = getRateLimitErrorMessage({
            errorCode,
            skipDefaultMessage: true,
            errorContext: {
                flow: RateLimitFlows.UPLOAD_USER_IMAGE,
            },
        });
        const moderationErrorMessage = getModerationErrorMessage({
            errorCode,
            skipDefaultMessage: true,
            errorContext: {
                flow: ModerationFlows.UPLOAD_USER_IMAGE,
            },
        });
        const imageErrorMessage = getImageErrorMessage({
            errorCode,
            fallbackMessage: responseErrorMessage,
        });
        const errorMessage = rateLimitErrorMessage ||
            moderationErrorMessage ||
            imageErrorMessage ||
            responseErrorMessage ||
            t("error:fallbackError");
        return { errorMessage, errorCode };
    }, [
        getImageErrorMessage,
        getModerationErrorMessage,
        getRateLimitErrorMessage,
        t,
    ]);
    const onUserUploadImage = useCallback(async (params) => {
        try {
            setImageError(null);
            const result = await dispatch(uploadConversationImage(params)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            if (error?.message === ABORT_ERROR_MESSAGE) {
                return {
                    data: null,
                    error: ABORT_ERROR_MESSAGE,
                    errorPayload: error,
                };
            }
            const errorPayload = error;
            const { errorMessage, errorCode } = resolveImageError(errorPayload);
            setImageError({
                errorMessage,
                errorCode: errorCode || undefined,
            });
            return {
                data: null,
                error: errorMessage,
                errorPayload,
            };
        }
    }, [dispatch, resolveImageError]);
    const onUserDeleteImage = useCallback(async (params) => {
        try {
            setImageError(null);
            const result = await dispatch(deleteConversationImage(params)).unwrap();
            return { data: result, error: null };
        }
        catch (error) {
            const errorPayload = error;
            const { errorMessage, errorCode } = resolveImageError(errorPayload);
            setImageError({
                errorMessage,
                errorCode: errorCode || undefined,
            });
            return {
                data: null,
                error: errorMessage,
                errorPayload,
            };
        }
    }, [dispatch, resolveImageError]);
    const resetError = useCallback(() => {
        setImageError(null);
    }, []);
    const resetImageUploadState = useCallback(() => {
        resetError();
        void dispatch(resetConversationImageUploadState());
    }, [dispatch, resetError]);
    return {
        isUploading: isImageUploading,
        errorMessage: imageError?.errorMessage ?? null,
        errorCode: imageError?.errorCode ?? null,
        onUserUploadImage,
        onUserDeleteImage,
        resetError,
        resetImageUploadState,
    };
};
